const en = {
  // Navigation
  nav: {
    features: 'Features',
    useCases: 'Use Cases',
    pricing: 'Pricing',
    blog: 'Blog',
    about: 'About',
    consulting: 'Consulting',
    login: 'Log in',
    startFree: 'Start Free →',
    dashboard: 'Dashboard',
    teams: 'Teams',
    sprints: 'Sprints',
    kpis: 'KPIs',
    users: 'Users',
    profileSettings: 'Profile Settings',
    companySettings: 'Company Settings',
    subscription: 'Subscription',
    superAdminPanel: 'SuperAdmin Panel',
    logout: 'Log out',
  },

  // Use cases dropdown
  useCases: {
    smallStartups: 'Small Startups (1-20)',
    smb: 'SMB (21-200)',
    enterprise: 'Enterprise',
    itTeams: 'IT Teams',
    operations: 'Operations',
    sales: 'Sales',
    managementBoard: 'Management Board',
    projectManagement: 'Project Management',
    publicInstitutions: 'Public Institutions',
    nonprofits: 'Nonprofits',
    hrTeams: 'HR Teams',
    manufacturing: 'Manufacturing',
  },

  // Landing page
  landing: {
    badge: 'Track KPIs in under 60 seconds',
    heroTitle1: 'Your Company Performance,',
    heroTitle2: 'Visualized',
    heroDescription: 'The modern way for CEOs and teams to track KPIs, spot risks early, and keep everyone aligned on what matters most.',
    getStarted: 'Get Started Free',
    viewDemo: 'View Demo',
    featuresTitle: 'Everything you need to track performance',
    featuresSubtitle: 'Built for modern companies who want visibility without complexity.',
    fromBlog: 'From Our Blog',
    blogTitle: 'Expert Insights on KPI Management',
    blogSubtitle: 'Learn proven strategies to build effective KPI systems and drive performance.',
    readArticle: 'Read article',
    viewAllArticles: 'View All Articles',
    ctaTitle: 'Ready to see your performance clearly?',
    ctaSubtitle: 'Start now! Use Outsprinter to stay on track and grow your business',
    startTrial: 'Start Your Free Trial',
  },

  // Features
  features: {
    kpiManagement: 'KPI Management',
    kpiManagementDesc: 'Define, track, and visualize key performance indicators across your entire organization.',
    realtimeDashboards: 'Real-time Dashboards',
    realtimeDashboardsDesc: 'Beautiful charts and graphs that update instantly as teams enter weekly data.',
    sprintPlanning: 'Sprint Planning',
    sprintPlanningDesc: 'Plan and manage team sprints with task assignments, deadlines, and progress tracking.',
    recurringSprintsTitle: 'Recurring Sprints',
    recurringSprintsDesc: 'Auto-create weekly or bi-weekly sprints to maintain consistent execution rhythms.',
    sprintRetroTitle: 'Sprint Retrospectives',
    sprintRetroDesc: 'Document learnings, capture what went well, and track action items after each sprint.',
    roleBasedAccess: 'Role-based Access',
    roleBasedAccessDesc: 'CEOs see everything, managers focus on their teams, viewers stay informed.',
    aiAssistant: 'AI Assistant',
    aiAssistantDesc: 'Chat to plan better KPIs, sprint tasks, and get insights on team performance.',
    trendAnalysis: 'Trend Analysis',
    trendAnalysisDesc: 'Track progress over time with weekly trends and performance forecasting.',
    enterpriseSecurity: 'Enterprise Security',
    enterpriseSecurityDesc: 'Bank-grade encryption, GDPR compliance, and complete data isolation.',
  },

  // Footer
  footer: {
    description: 'The modern way for CEOs and teams to track KPIs, spot risks early, and keep everyone aligned on what matters most.',
    product: 'Product',
    useCases: 'Use Cases',
    resources: 'Resources',
    company: 'Company',
    legal: 'Legal',
    features: 'Features',
    pricing: 'Pricing',
    updates: 'Updates',
    blog: 'Blog',
    helpCenter: 'Help Center',
    faq: 'FAQ',
    aboutUs: 'About Us',
    consultingAdvisory: 'Consulting & Advisory',
    contact: 'Contact',
    press: 'Press',
    affiliates: 'Affiliates',
    termsOfService: 'Terms of Service',
    privacyNotice: 'Privacy Notice',
    cookiePolicy: 'Cookie Policy',
    copyright: '© 2026 Outsprinter. All rights reserved.',
    uptime: '99.9% Uptime',
  },

  // Pricing page
  pricing: {
    tryFree: 'Try free for 14 days',
    manageSubscription: 'Manage your subscription',
    chooseYourPlan: 'Choose your plan',
    yourSubscription: 'Your Subscription',
    subscribedSubtitle: "You're currently subscribed. Upgrade or manage your plan below.",
    unsubscribedSubtitle: 'All plans include the full Outsprinter feature set. Choose based on your team size.',
    monthly: 'Monthly',
    yearly: 'Yearly',
    save30: 'Save 30% compared to monthly',
    month: 'month',
    year: 'year',
    planLimits: 'Plan Limits',
    allFeaturesIncluded: 'All Features Included',
    bestFor: '🎯 Best for:',
    startFree: 'Start free',
    currentPlan: 'Current Plan',
    switchPlan: 'Switch Plan',
    subscribeNow: 'Subscribe Now',
    processing: 'Processing...',
    yourPlan: 'Your Plan',
    popular: 'Popular',
    enterpriseLine: 'Need more than 75 users, SSO, or API access?',
    contactUs: 'Contact us',
    forEnterprise: 'for Enterprise pricing.',
    needHelp: 'Need help? Contact our support team.',
    allPlansInclude: 'All plans include a 14-day free trial. No credit card required.',
    redirecting: 'Redirecting to checkout',
    redirectingDesc: "You'll be redirected to complete your subscription.",
    checkoutError: 'Failed to start checkout. Please try again.',
    // Plan names & descriptions
    starter: 'STARTER',
    starterDesc: 'For small teams getting started',
    professional: 'PROFESSIONAL',
    professionalDesc: 'For growing companies',
    business: 'BUSINESS',
    businessDesc: 'For larger organizations',
    // Limits
    teams3: '3 teams',
    kpis20: '20 KPIs',
    users5: '5 users',
    teams10: '10 teams',
    kpis75: '75 KPIs',
    users25: '25 users',
    teamsUnlimited: 'Unlimited teams',
    kpisUnlimited: 'Unlimited KPIs',
    users75: '75 users',
    // Suitable
    smallBusinesses: 'Small businesses',
    agencies: 'Agencies',
    employees520: '5–20 employees',
    scalingStartups: 'Scaling startups',
    employees20100: '20–100 employees',
    multiDepartment: 'Multi-department',
    employees100: '100+ employees',
    leadershipTeams: 'Leadership teams',
    investorReporting: 'Investor reporting',
    // Shared features
    weeklyKpiTracking: 'Weekly KPI tracking',
    dashboardCharts: 'Dashboard with charts & graphs',
    fullKpiTree: 'Full KPI Tree view',
    autoStatusLogic: 'Automatic status logic',
    goalPlanner: 'Goal Planner (targets)',
    historicalData: 'Historical data & trends',
    sprintPlanning: 'Sprint planning & tracking',
    taskAssignments: 'Task assignments',
    recurringSprintsFeature: 'Recurring sprints',
    taskCarryOver: 'Task carry-over',
    sprintRetrospectives: 'Sprint retrospectives',
    sprintAnalytics: 'Sprint analytics',
    teamStructure: 'Team structure (org chart)',
    roleBasedAccessControl: 'Role-based access control',
    csvExport: 'CSV export',
    departmentPerformance: 'Department performance view',
    aiAssistantPlanning: 'AI Assistant for planning',
    emailSupport: 'Email support',
  },

  // About page
  about: {
    title: 'About Outsprinter',
    heroDescription: 'Outsprinter is a performance management platform built for leaders who want',
    realClarity: 'real clarity',
    realAccountability: 'real accountability',
    realResults: 'real results',
    missionIntro: 'We created Outsprinter to solve a problem that exists in almost every company: goals are set, but progress is unclear, fragmented, and often based on gut feeling instead of real data.',
    missionTitle: 'Our mission is simple —',
    missionDesc: 'to help companies manage their performance in a transparent, data-driven and consistent way, so that every team knows:',
    missionPoint1: 'what matters,',
    missionPoint2: 'how they are performing,',
    missionPoint3: 'and what needs attention right now.',
    executionRhythm: 'Outsprinter turns strategy into a weekly execution rhythm.',
    replacesExcel: 'It replaces scattered Excel files, forgotten KPIs and blurred accountability with:',
    kpiTree: 'a visual KPI Tree,',
    weeklyTracking: 'weekly tracking,',
    autoStatus: 'automatic status logic,',
    clearDashboards: 'and clear dashboards for leadership and teams.',
    resultStatement: 'The result is not just better reporting — but stronger focus, faster decisions, and measurable outcomes.',
    whyTitle: 'Why Outsprinter Was Created',
    whyIntro: "Outsprinter wasn't born from theory or consulting frameworks. It was born from",
    realExperience: 'real operating experience',
    whyIntroEnd: 'inside growing companies.',
    founderExperience: 'As a founder and CEO, I experienced firsthand how quickly organizations lose focus when:',
    whyPoint1: 'data lives in different tools,',
    whyPoint2: 'KPIs are tracked irregularly,',
    whyPoint3: "teams don't see the bigger picture,",
    whyPoint4: 'and leadership reacts too late.',
    solutionNotMeetings: 'The solution was not more meetings.',
    solutionWas: 'The solution was a',
    simpleVisual: 'simple, visual and disciplined performance system',
    thatForces: 'that forces:',
    weeklyReflection: 'weekly reflection,',
    honestNumbers: 'honest numbers,',
    clearOwnership: 'and clear ownership.',
    builtToEnsure: 'Outsprinter was built to make sure that:',
    ensurePoint1: "strategy doesn't stay on slides,",
    ensurePoint2: "goals don't die in spreadsheets,",
    ensurePoint3: "and execution becomes part of the company's weekly heartbeat.",
    founder: 'Founder',
    connectLinkedIn: 'Connect on LinkedIn',
    founderBio: 'Arti Kütt is an entrepreneur who has built startups, led international technology and e-commerce companies, and actively invests as a local angel investor. His work focuses on building scalable businesses with clear execution and measurable results.',
    philosophyTitle: 'Our Philosophy',
    belief1: 'What gets measured weekly, gets improved.',
    belief2: 'What is transparent, gets owned.',
    belief3: 'What is visual, gets understood.',
    belief4: 'What is tracked consistently, delivers results.',
    philosophyStatement: "We are building Outsprinter for founders, CEOs and leadership teams who don't want excuses, chaos, or delayed reactions — but want",
    controlClarityResults: 'control, clarity and results',
    visionTitle: 'Our Vision',
    visionStatement: 'To become the most simple and trusted performance operating system for leadership teams across Europe and beyond — where',
    strategyExecution: 'strategy, execution and accountability',
    liveInOnePlace: 'live in one place.',
  },

  // Auth page
  auth: {
    welcomeBack: 'Welcome back',
    createAccount: 'Create your account',
    loginDesc: 'Enter your credentials to access your dashboard',
    signupDesc: 'Get started with Outsprinter in seconds',
    inviteDesc: 'Complete your account setup to join your team',
    continueWithGoogle: 'Continue with Google',
    orContinueWith: 'Or continue with',
    fullName: 'Full Name',
    email: 'Email',
    password: 'Password',
    agreeToTerms: 'I agree to the',
    termsOfService: 'Terms of Service',
    and: 'and',
    privacyPolicy: 'Privacy Policy',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    createAccountBtn: 'Create account',
    creatingAccount: 'Creating account...',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    signUp: 'Sign up',
    resetPassword: 'Reset your password',
    resetDesc: "Enter your email address and we'll send you a link to reset your password.",
    resetSent: 'Check your email for a password reset link.',
    resetSentTo: "We've sent a password reset link to",
    checkSpam: "Didn't receive it? Check your spam folder or try again.",
    tryDifferentEmail: 'Try different email',
    done: 'Done',
    emailAddress: 'Email address',
    sendResetLink: 'Send Reset Link',
    sendingReset: 'Sending...',
  },

  // Contact page
  contact: {
    title: 'Contact Outsprinter',
    subtitle: "We're here to help you execute better.",
    description: "Whether you're evaluating Outsprinter, need support, or want to explore how it fits your organization, our team is ready to help.",
    getInTouch: '📩 Get in Touch',
    formIntro: 'Use the contact form to reach us regarding:',
    productQuestions: 'Product questions',
    salesInquiries: 'Sales inquiries',
    enterprisePricing: 'Enterprise pricing',
    onboardingTraining: 'Onboarding & training',
    supportRequests: 'Support requests',
    partnerships: 'Partnerships',
    name: 'Name',
    yourName: 'Your name',
    email: 'Email',
    yourEmail: 'your@email.com',
    company: 'Company',
    yourCompany: 'Your company',
    message: 'Message',
    howCanWeHelp: 'How can we help?',
    sendMessage: 'Send Message',
    responseTime: 'We typically respond within one business day.',
    salesDemos: '🧭 Sales & Demos',
    salesDesc: 'Interested in seeing Outsprinter in action or discussing pricing?',
    bookDemo: 'Book a Demo',
    bookLiveDemo: 'Book a live demo',
    requestEnterprise: 'Request enterprise pricing',
    customerSupport: '🛠️ Customer Support',
    supportDesc: 'Already a customer and need help?',
    inAppSupport: 'In-app support',
    emailSupportLabel: 'Email support',
    helpCenterResources: 'Help Center resources',
    visitHelpCenter: 'Visit Help Center',
    generalInquiries: '📧 General Inquiries',
    generalDesc: 'For partnerships, media, or general questions:',
    responseWithin24h: 'Response time: within 24 hours',
    ourApproach: '🌍 Our Approach',
    supportShouldBe: 'We believe support should be:',
    fast: 'Fast',
    clear: 'Clear',
    human: 'Human',
    focusedOnOutcomes: 'Focused on outcomes',
    noTicketBlackHoles: 'No ticket black holes. No unnecessary back-and-forth.',
    letsTalk: "📣 Let's talk about execution.",
    contactUsBtn: 'Contact Us',
  },

  // FAQ page
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Clear answers to common questions about Outsprinter.',
    stillHaveQuestions: "📣 Still have questions? We're here to help.",
    contactSupport: 'Contact Support',
    bookDemo: 'Book a Demo',
    // Categories
    productFunctionality: '🧠 Product & Functionality',
    kpisSprints: '📊 KPIs & Sprints',
    aiAssistant: '🧠 AI Assistant',
    usersAccounts: '👥 Users & Accounts',
    pricingBilling: '💳 Pricing & Billing',
    securityPrivacy: '🔐 Security & Privacy',
    support: '🛠️ Support',
    // Q&A - Product
    whatIsOutsprinter: 'What is Outsprinter?',
    whatIsOutsprinterA: 'Outsprinter is a B2B execution platform that helps companies set KPIs, plan weekly sprints, track execution, and gain AI-powered insights — all in one place.',
    isProjectMgmt: 'Is Outsprinter a project management tool?',
    isProjectMgmtA: 'No. Outsprinter focuses on execution, outcomes, and KPIs. It complements project tools like Jira or Asana rather than replacing them.',
    whatMakesDifferent: 'What makes Outsprinter different?',
    whatMakesDifferentA: 'Outsprinter connects strategy, KPIs, weekly cadence, sprints, and AI insights into one execution operating system.',
    // Q&A - KPIs
    howOftenUpdate: 'How often should KPIs be updated?',
    howOftenUpdateA: 'Outsprinter is built around a weekly cadence. Weekly updates provide fast feedback and better execution discipline.',
    howManyKpis: 'How many KPIs should we track?',
    howManyKpisA: 'Most teams perform best with 3–7 KPIs per team or department.',
    canKpisRollUp: 'Can KPIs roll up to company-level goals?',
    canKpisRollUpA: 'Yes. The KPI Tree structure allows full roll-up visibility.',
    // Q&A - AI
    whatDoesAiDo: 'What does the AI Assistant do?',
    whatDoesAiDoA: 'The AI Assistant analyzes KPIs and sprints, detects risks, generates summaries, and suggests improvements.',
    doesAiDecide: 'Does AI make decisions automatically?',
    doesAiDecideA: 'No. AI provides insights and recommendations — humans stay in control.',
    isDataUsedForAi: 'Is our data used to train public AI models?',
    isDataUsedForAiA: 'No. Customer data is isolated and never used for public model training.',
    // Q&A - Users
    canInviteExternal: 'Can we invite external users?',
    canInviteExternalA: 'Yes, admins can invite users and assign roles.',
    multipleCompanies: 'Can users belong to multiple companies?',
    multipleCompaniesA: 'Each user belongs to one company at a time for data isolation and security.',
    roleBasedSupported: 'Is role-based access supported?',
    roleBasedSupportedA: 'Yes. Outsprinter supports admin, manager, user, and viewer roles.',
    // Q&A - Pricing
    isFreeTrial: 'Is there a free trial?',
    isFreeTrialA: 'Yes. Outsprinter offers a free trial with core features.',
    canChangePlans: 'Can we change plans later?',
    canChangePlansA: 'Yes. Plans can be upgraded or downgraded at any time.',
    nonprofitDiscounts: 'Do you offer nonprofit discounts?',
    nonprofitDiscountsA: 'Yes. Verified nonprofits receive discounted pricing.',
    // Q&A - Security
    isGdpr: 'Is Outsprinter GDPR compliant?',
    isGdprA: 'Yes. Outsprinter is designed with GDPR principles in mind.',
    whereDataStored: 'Where is data stored?',
    whereDataStoredA: 'Data is stored securely in cloud infrastructure with encryption at rest and in transit.',
    canExportDelete: 'Can we export or delete our data?',
    canExportDeleteA: 'Yes. Admins can export data and request full deletion.',
    // Q&A - Support
    whatSupportOptions: 'What support options are available?',
    whatSupportOptionsA: 'Email support, in-app help, onboarding sessions, and enterprise support options.',
    offerOnboarding: 'Do you offer onboarding?',
    offerOnboardingA: 'Yes. Guided onboarding is available for teams and enterprises.',
  },

  // Help Center page
  helpCenter: {
    title: 'Help Center',
    subtitle: 'Everything you need to get value from Outsprinter — fast.',
    description: "Whether you're a CEO, team lead, or individual contributor, you'll find clear guidance, step-by-step instructions, and practical examples.",
    structureTitle: '🔍 How the Help Center Is Structured',
    needMoreHelp: '📣 Need More Help?',
    cantFind: "If you can't find what you're looking for:",
    contactFromApp: 'Contact support directly from the app',
    emailSupport: 'Email our support team',
    bookOnboarding: 'Book a guided onboarding session',
    contactSupportBtn: 'Contact Support',
    bookOnboardingBtn: 'Book Onboarding Session',
    // Categories
    gettingStartedTitle: 'Getting Started',
    gettingStartedDesc: 'Perfect for new users and new teams.',
    kpiGuidesTitle: 'KPI Manager Guides',
    kpiGuidesDesc: 'Learn how to design and track meaningful KPIs.',
    sprintManagerTitle: 'Team Sprint Manager',
    sprintManagerDesc: 'Master weekly execution and sprint planning.',
    aiAssistantTitle: 'Smart AI Assistant',
    aiAssistantDesc: 'Get the most out of AI-powered insights.',
    dashboardsTitle: 'Dashboards & Reporting',
    dashboardsDesc: 'Learn how to interpret and use dashboards.',
    userCompanyMgmtTitle: 'User & Company Management',
    userCompanyMgmtDesc: 'Admin-level configuration and control.',
    securityTitle: 'Security, Privacy & Compliance',
    securityDesc: 'Understand how Outsprinter protects your data.',
    troubleshootingTitle: 'Troubleshooting & Support',
    troubleshootingDesc: 'Quick answers to common issues.',
    // Topics
    gs1: 'Creating your account and company',
    gs2: 'Inviting users and assigning roles',
    gs3: 'Setting up your first KPIs',
    gs4: 'Understanding the KPI Tree',
    gs5: 'Planning your first weekly sprint',
    kpi1: 'How to define effective KPIs',
    kpi2: 'Company vs. department KPIs',
    kpi3: 'Weekly KPI update best practices',
    kpi4: 'KPI thresholds (green / yellow / red)',
    kpi5: 'KPI ownership and accountability',
    kpi6: 'KPI trends and forecasting',
    sp1: 'How to plan weekly sprints',
    sp2: 'Choosing sprint priorities',
    sp3: 'Sprint reviews and retrospectives',
    sp4: 'Avoiding overcommitment',
    sp5: 'Measuring sprint success',
    ai1: 'How AI generates insights',
    ai2: 'AI-suggested KPIs and sprints',
    ai3: 'Understanding AI risk alerts',
    ai4: 'AI summaries and reports',
    ai5: 'How AI improves over time',
    dash1: 'Company-wide dashboards',
    dash2: 'Team dashboards',
    dash3: 'Board-level views',
    dash4: 'Forecast and trend analysis',
    dash5: 'Exporting reports (PDF, CSV)',
    ucm1: 'Managing users and roles',
    ucm2: 'Company profile settings',
    ucm3: 'Permissions and access control',
    ucm4: 'Billing and subscription management',
    ucm5: 'Audit logs',
    sec1: 'Data security overview',
    sec2: 'GDPR compliance',
    sec3: 'Access logging and audit trails',
    sec4: 'Data export and deletion',
    ts1: 'Login and access issues',
    ts2: 'KPI update problems',
    ts3: 'Sprint visibility issues',
    ts4: 'Notification troubleshooting',
  },

  // Use case shared
  useCaseCta: {
    startFreeTrial: 'Start Free Trial →',
    bookDemo: 'Book a Demo',
  },

  // Terms of Service
  terms: {
    title: 'Terms of Service',
    effectiveDate: 'Effective date',
    intro: 'Welcome to Outsprinter. These Terms of Service ("Terms") govern your access to and use of the Outsprinter platform, website, and services ("Service").',
    agreement: 'By creating an account or using the Service, you agree to be bound by these Terms.',
    s1Title: '1. Definitions',
    s1d1: 'refers to the company operating the Outsprinter platform.',
    s1d2: 'refers to the individual or entity using the Service.',
    s1d3: 'means a registered user account.',
    s1d4: 'means the organization created within Outsprinter.',
    s1d5: 'means all data, KPIs, text, and information entered into the Service.',
    s2Title: '2. Use of the Service',
    s2p1: 'You may use the Service only in accordance with these Terms and applicable laws.',
    s2agree: 'You agree to',
    s2a1: 'Provide accurate account information',
    s2a2: 'Maintain the security of your login credentials',
    s2a3: 'Use the Service only for lawful business purposes',
    s2mustNot: 'You must not',
    s2n1: 'Access data of another company',
    s2n2: 'Reverse engineer or misuse the platform',
    s2n3: 'Interfere with system security or availability',
    s3Title: '3. Accounts & Access',
    s3p1: 'Each user must have a unique account',
    s3p2: 'Users belong to a single company workspace',
    s3p3: 'Admins are responsible for managing access and permissions',
    s3p4: 'You are responsible for all activity under your account',
    s4Title: '4. Subscriptions & Billing',
    s4p1: 'Some features require a paid subscription',
    s4p2: 'Billing is handled on a recurring basis (monthly or annually)',
    s4p3: 'Prices are shown excluding VAT unless stated otherwise',
    s4p4: 'Subscriptions can be upgraded or downgraded according to plan rules',
    s4warning: 'Failure to pay may result in suspension of the Service.',
    s5Title: '5. Free Trials',
    s5intro: 'Outsprinter may offer free trials',
    s5p1: 'Trials are time-limited',
    s5p2: 'Feature access may be restricted',
    s5p3: 'Trial accounts may convert to paid upon expiration',
    s6Title: '6. Customer Content',
    s6p1: 'You retain ownership of all data and content you submit.',
    s6p2: 'You grant Outsprinter a limited license to',
    s6l1: 'Store',
    s6l2: 'Process',
    s6l3: 'Display',
    s6p3: 'your content solely for providing the Service.',
    s6noAi: 'We do not use customer data to train public AI models.',
    s7Title: '7. AI Features',
    s7p1: 'Outsprinter provides AI-generated insights and recommendations.',
    s7p2: 'You acknowledge',
    s7l1: 'AI outputs are informational only',
    s7l2: 'Final decisions remain your responsibility',
    s7l3: 'AI does not replace professional judgment',
    s8Title: '8. Data Protection & Privacy',
    s8p1: 'We process personal data in accordance with our Privacy Notice and applicable data protection laws, including GDPR.',
    s9Title: '9. Security',
    s9p1: 'We implement reasonable technical and organizational measures to protect your data. However, no system can guarantee absolute security.',
    s10Title: '10. Termination',
    s10p1: 'You may terminate your account at any time.',
    s10p2: 'We may suspend or terminate access if',
    s10l1: 'These Terms are violated',
    s10l2: 'Required by law',
    s10l3: 'Payment obligations are not met',
    s10p3: 'Upon termination, data may be deleted according to retention policies.',
    s11Title: '11. Limitation of Liability',
    s11intro: 'To the maximum extent permitted by law',
    s11l1: 'Outsprinter is not liable for indirect or consequential damages',
    s11l2: 'Total liability shall not exceed fees paid in the previous 12 months',
    s12Title: '12. Changes to Terms',
    s12p1: 'We may update these Terms from time to time. Continued use of the Service constitutes acceptance of the updated Terms.',
    s13Title: '13. Governing Law',
    s13p1: 'These Terms are governed by the laws of the European Union and applicable local jurisdiction.',
    s14Title: '14. Contact',
    s14p1: 'For questions regarding these Terms',
  },

  // Privacy Notice
  privacy: {
    title: 'Privacy Notice',
    effectiveDate: 'Effective date',
    intro: 'This Privacy Notice explains how Outsprinter collects, uses, and protects personal data.',
    s1Title: '1. Data Controller',
    s1p1: 'Outsprinter acts as the data controller for personal data processed through the Service.',
    s2Title: '2. Data We Collect',
    s2intro: 'We may collect',
    s2l1: 'Name',
    s2l2: 'Email address',
    s2l3: 'Company information',
    s2l4: 'Login and usage data',
    s2l5: 'Billing details (processed via payment providers)',
    s3Title: '3. How We Use Data',
    s3intro: 'We use personal data to',
    s3l1: 'Provide and operate the Service',
    s3l2: 'Authenticate users',
    s3l3: 'Process payments',
    s3l4: 'Improve product functionality',
    s3l5: 'Provide customer support',
    s3l6: 'Meet legal obligations',
    s4Title: '4. Legal Basis for Processing',
    s4intro: 'We process data based on',
    s4l1: 'Contractual necessity',
    s4l2: 'Legitimate business interests',
    s4l3: 'Legal obligations',
    s4l4: 'User consent (where required)',
    s5Title: '5. Data Sharing',
    s5noSell: 'We do not sell personal data.',
    s5share: 'We may share data with',
    s5l1: 'Hosting and infrastructure providers',
    s5l2: 'Payment processors',
    s5l3: 'Analytics services',
    s5l4: 'Legal authorities when required',
    s5bound: 'All partners are contractually bound to protect data.',
    s6Title: '6. Data Retention',
    s6intro: 'We retain personal data only as long as necessary to',
    s6l1: 'Provide the Service',
    s6l2: 'Comply with legal requirements',
    s7Title: '7. Your Rights (GDPR)',
    s7intro: 'You have the right to',
    s7l1: 'Access your data',
    s7l2: 'Correct inaccurate data',
    s7l3: 'Request deletion',
    s7l4: 'Restrict or object to processing',
    s7l5: 'Export your data',
    s7request: 'Requests can be made via',
    s8Title: '8. Security Measures',
    s8intro: 'We use',
    s8l1: 'Encryption in transit and at rest',
    s8l2: 'Access controls',
    s8l3: 'Audit logging',
    s8l4: 'Secure cloud infrastructure',
    s9Title: '9. International Transfers',
    s9p1: 'If data is transferred outside the EU, appropriate safeguards are applied.',
    s10Title: '10. Changes',
    s10p1: 'We may update this Privacy Notice. Material changes will be communicated.',
    s11Title: '11. Contact',
    s11p1: 'For privacy-related questions',
  },

  // Cookie Policy
  cookie: {
    title: 'Cookie Policy',
    effectiveDate: 'Effective date',
    intro: 'This Cookie Policy explains how Outsprinter uses cookies and similar technologies.',
    s1Title: '1. What Are Cookies?',
    s1p1: 'Cookies are small text files stored on your device to improve website functionality and user experience.',
    s2Title: '2. Types of Cookies We Use',
    essential: 'Essential Cookies',
    essentialFor: 'Required for',
    e1: 'Authentication',
    e2: 'Security',
    e3: 'Core functionality',
    cannotDisable: 'These cannot be disabled.',
    analytics: 'Analytics Cookies',
    analyticsFor: 'Used to understand',
    a1: 'Platform usage',
    a2: 'Feature performance',
    a3: 'Traffic patterns',
    analyticsHelp: 'These help us improve the Service.',
    functional: 'Functional Cookies',
    functionalFor: 'Used to',
    f1: 'Remember preferences',
    f2: 'Improve usability',
    s3Title: '3. Managing Cookies',
    s3p1: 'You can manage or disable cookies through your browser settings.',
    s3note: 'Note: Disabling essential cookies may impact functionality.',
    s4Title: '4. Third-Party Cookies',
    s4p1: 'We may use trusted third-party tools (e.g. analytics providers). These providers comply with applicable data protection laws.',
    s5Title: '5. Consent',
    s5p1: 'Where required, cookies are used only after obtaining your consent via a cookie banner.',
    s6Title: '6. Updates',
    s6p1: 'This Cookie Policy may be updated from time to time.',
    s7Title: '7. Contact',
    s7p1: 'For cookie-related questions',
  },

  // Press page
  press: {
    title: 'Press',
    subtitle: 'Resources for journalists, media, and partners.',
    description: 'This page provides official information, brand assets, and contact details for press and media inquiries related to Outsprinter.',
    inquiriesTitle: '📰 Press Inquiries',
    inquiriesDesc: 'For media requests, interviews, or press coverage, please contact:',
    pressContact: 'Press Contact',
    responseTime: 'We aim to respond to press inquiries within 48 hours.',
    brandTitle: '🎨 Brand Assets',
    brandDesc: 'Available upon request:',
    asset1: 'Company logo (light & dark versions)',
    asset2: 'Product screenshots',
    asset3: 'Founder photos',
    asset4: 'Brand guidelines',
    brandNote: 'Please use approved assets only and do not alter logos or colors.',
    overviewTitle: '🏢 Company Overview (Boilerplate)',
    overviewP1: 'Outsprinter is a B2B execution platform that helps companies turn strategy into measurable results. The platform combines KPI management, weekly sprint execution, real-time dashboards, and AI-driven insights to help teams stay aligned, focused, and accountable.',
    overviewP2: 'Outsprinter is used by startups, scale-ups, enterprises, public institutions, and nonprofits to improve execution quality and decision-making.',
    topicsTitle: '🗞️ Topics We Comment On',
    topic1: 'Execution and performance management',
    topic2: 'KPI-driven leadership',
    topic3: 'Weekly cadence and sprint execution',
    topic4: 'AI in decision-making',
    topic5: 'Modern management practices',
    topic6: 'Scaling teams and organizations',
    ctaTitle: '📣 Covering execution, leadership, or SaaS innovation?',
    ctaButton: 'Contact Press Team',
  },

  // Affiliates page
  affiliates: {
    title: 'Affiliate Program',
    subtitle: 'Earn by helping teams execute better.',
    description: 'The Outsprinter Affiliate Program rewards partners who introduce companies to a better way of executing strategy, tracking KPIs, and running weekly sprints.',
    whoTitle: '🤝 Who the Program Is For',
    whoDesc: 'The affiliate program is ideal for:',
    audience1: 'Consultants & coaches',
    audience2: 'Fractional CEOs & COOs',
    audience3: 'Advisors & mentors',
    audience4: 'Agencies',
    audience5: 'Content creators',
    audience6: 'SaaS communities',
    whoNote: 'If you work with leadership teams, Outsprinter is a natural fit.',
    howTitle: '💰 How It Works',
    step1: 'You refer a company to Outsprinter',
    step2: 'They start a paid subscription',
    step3: 'You earn recurring commission',
    howNote: 'Simple, transparent, and scalable.',
    commTitle: '💸 Commission Model',
    comm1: 'Competitive recurring commission per paying customer',
    comm2: 'Long-term revenue share',
    comm3: 'No cap on earnings',
    comm4: 'Transparent tracking dashboard',
    commNote: 'Exact commission details are shared upon approval.',
    whyTitle: '🧩 Why Partner with Outsprinter',
    why1: 'High-value B2B product',
    why2: 'Clear value proposition',
    why3: 'Strong retention potential',
    why4: 'Growing demand for execution tools',
    why5: 'Dedicated partner support',
    whyNote: 'You help companies win — and get rewarded for it.',
    startTitle: '🚀 Getting Started',
    startDesc: 'To apply',
    startStep1: 'Fill out the affiliate application',
    startStep2: 'Get approved',
    startStep3: 'Receive your unique referral link',
    startStep4: 'Start sharing Outsprinter',
    applyBtn: 'Apply to Affiliate Program',
    ctaTitle: '📣 Help companies execute better — and grow with us.',
    ctaBtn: 'Become an Affiliate',
  },

  // Updates page
  updates: {
    title: 'Updates',
    badge: 'Product Updates',
    subtitle: 'Product improvements, new features, and continuous innovation — transparently shared.',
    introP1: 'Outsprinter is built as a continuously evolving execution platform. We ship improvements regularly to make KPI tracking, sprint execution, and AI-driven insights faster, smarter, and more reliable.',
    introP2: "This page keeps you informed about what's new, what's improved, and what's coming next.",
    latestTitle: 'Latest Updates',
    latestDesc: 'Stay current with every release. Each update focuses on real customer needs: better clarity, faster execution, and deeper insights.',
    latest1: 'Enhanced KPI Tree visualization for large organizations',
    latest2: 'Faster weekly sprint planning with AI-assisted prioritization',
    latest3: 'Improved dashboard performance and real-time refresh',
    latest4: 'New AI insights for execution risks and performance trends',
    latest5: 'Expanded admin controls for company and user management',
    latestNote: 'Every release is designed to reduce friction and improve decision-making.',
    releaseTitle: 'Release Notes',
    releaseDesc: 'Clear, structured, and transparent. Each release note includes:',
    whatsNew: "What's new",
    newFeatures: 'New features',
    newCapabilities: 'New capabilities',
    whatsImproved: "What's improved",
    perfEnhancements: 'Performance enhancements',
    uxImprovements: 'UX improvements',
    bugFixes: 'Bug fixes',
    securityUpdates: 'Security updates',
    stabilityFixes: 'Stability fixes',
    aiTitle: 'AI Feature Updates',
    aiDesc: "Smarter execution, every month. Outsprinter's AI Assistant is continuously trained and improved.",
    ai1: 'Smarter KPI suggestions based on company size and industry',
    ai2: 'Improved sprint forecasting accuracy',
    ai3: 'Better anomaly detection in KPI trends',
    ai4: 'More actionable leadership insights',
    ai5: 'Enhanced natural-language summaries for weekly reviews',
    aiGoal: 'Our goal: less manual analysis, more confident decisions.',
    betaTitle: 'Beta Program',
    betaDesc: "Get early access to what's next. Join the Outsprinter Beta Program to:",
    betaBenefitsTitle: 'Benefits',
    beta1: 'Test new features before public release',
    beta2: 'Influence product direction',
    beta3: 'Provide direct feedback to the product team',
    beta4: 'Gain early access to advanced AI capabilities',
    betaGetTitle: 'Beta users get',
    betaGet1: 'Early feature flags',
    betaGet2: 'Priority feedback channels',
    betaGet3: 'Early adopter recognition',
    betaNote: '👉 Available for Pro, Business, and Enterprise customers.',
    perfTitle: 'Performance & Stability Improvements',
    perfDesc: 'We continuously invest in:',
    perf1: 'Faster dashboard load times',
    perf2: 'Improved system reliability',
    perf3: 'Scalable infrastructure for growing teams',
    perf4: 'Enhanced data security and monitoring',
    perfNote: 'Every update is tested to ensure minimal disruption and maximum stability.',
    secTitle: 'Security & Compliance Updates',
    secDesc: 'Security is treated as a core product feature. Updates include:',
    sec1: 'Regular dependency updates',
    sec2: 'Infrastructure hardening',
    sec3: 'Improved access logging',
    sec4: 'GDPR and data protection enhancements',
    secNote: 'Enterprise and compliance-focused customers are notified of relevant changes proactively.',
    roadmapTitle: "What's Coming Next",
    roadmapDesc: 'Our near-term roadmap focuses on:',
    road1: 'Advanced AI forecasting for KPIs',
    road2: 'Cross-team dependency visualization',
    road3: 'Improved board-level reporting',
    road4: 'Deeper integrations (CRM, HR, BI tools)',
    road5: 'Custom KPI frameworks by industry',
    roadmapNote: 'We build with customer input — and always with execution quality in mind.',
    ctaTitle: 'Stay ahead with a platform that never stands still.',
  },

  // Consulting page
  consulting: {
    title: 'Consulting',
    badge: 'Advisory & Implementation',
    heroTitle: 'Turning Strategy into Real Execution',
    heroDesc: 'Many companies know what they want to achieve, but struggle with execution. Our consultants help you implement new internal processes that actually work in day-to-day management — not just in presentations or documents.',
    heroDesc2: 'We help you build a management system that creates',
    clarity: 'clarity',
    accountability: 'accountability',
    results: 'measurable business results',
    whatWeDoTitle: 'What We Do',
    whatWeDoDesc: 'Comprehensive consulting services to transform your strategy into executable reality',
    service1Title: 'Management & KPI Framework Design',
    service1Desc: 'We help you define clear objectives (OKRs, KPIs) across the entire organization — from company level to departments and teams. The result is a transparent structure where everyone understands how their work contributes to business outcomes.',
    service2Title: 'Process Design & Implementation',
    service2Desc: 'We analyze your current workflows and help design and roll out new processes that:',
    service2b1: 'Improve accountability and ownership',
    service2b2: 'Reduce manual work and ambiguity',
    service2b3: 'Create disciplined, repeatable execution',
    service3Title: 'Leadership & Team Enablement',
    service3Desc: 'Even the best processes fail if people don\'t use them. We work closely with leadership and department heads to:',
    service3b1: 'Align leaders around why the change matters',
    service3b2: 'Train managers to use KPIs in daily leadership',
    service3b3: 'Embed the system into the company\'s management culture',
    service4Title: 'Measurement, Reporting & Continuous Improvement',
    service4Desc: 'We establish clear operating rhythms (e.g. weekly KPI reviews), dashboards, and visual reports that make it immediately clear:',
    service4b1: 'What is working',
    service4b2: 'Where performance is off track',
    service4b3: 'Where leadership intervention is required',
    whoTitle: 'Who This Is For',
    whoDesc: 'Our consulting is designed for organizations ready to transform their execution',
    target1: 'Growing B2B companies',
    target2: 'Production, service, and technology businesses',
    target3: 'Leadership teams seeking clarity and control',
    target4: 'Organizations with a strategy in place but weak or slow execution',
    approachTitle: 'Our Approach',
    approach1: 'Practical, not theoretical — focused on real-world usage',
    approach2: 'System-driven — goals, processes, people, and tools are aligned',
    approach3: 'Measurable — every change must impact results',
    approach4: 'Collaborative — we implement together with your leadership team',
    outcomesTitle: 'Outcomes Clients Experience',
    outcome1: 'Full transparency across the organization',
    outcome2: 'Clearer accountability at every level',
    outcome3: 'Faster and better decision-making',
    outcome4: 'Stronger execution and predictable results',
    outcome5: 'KPIs that function as real management tools, not just numbers',
    engageTitle: 'How the Engagement Starts',
    engage1: 'Introductory consultation',
    engage2: 'Assessment of current management practices',
    engage3: 'Clear recommendations and action plan',
    engage4: 'Hands-on implementation with leadership and teams',
    ctaTitle: 'Ready to Transform Your Execution?',
    ctaDesc: "Let's discuss how we can help you build a management system that delivers real results.",
    ctaBtn1: 'Book a Free Consultation',
    ctaBtn2: "Let's Talk About Your Challenges",
  },

  // Blog page
  blog: {
    seoTitle: 'KPI Management Blog - Expert Insights & Best Practices',
    seoDesc: 'Learn about KPI management, performance tracking, and business leadership. Expert articles on building effective KPI systems, weekly tracking, and data-driven decision making.',
    badge: 'KPI Management Insights',
    title: 'The Outsprinter Blog',
    subtitle: 'Expert insights on KPI management, performance tracking, and building data-driven organizations.',
    searchPlaceholder: 'Search articles...',
    allArticles: 'All Articles',
    noResults: 'No articles found matching your search.',
    clearFilters: 'Clear filters',
    ctaTitle: 'Ready to Transform Your KPI Management?',
    ctaDesc: 'Join hundreds of companies using Outsprinter to track performance weekly and make better decisions.',
    ctaBtn: 'Start Your Free Trial',
  },

  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    noResults: 'No results found',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    and: 'and',
  },

  // Use Case - Small Startups
  ucStartups: {
    seoTitle: 'Small Startups (1-20 Employees)',
    seoDesc: 'Build focus early. Execute fast. Scale with clarity. Outsprinter helps small teams stay aligned, focused, and execution-driven from day one.',
    badge: 'Small Startups (1–20 Employees)',
    heroTitle: 'Build focus early. Execute fast. Scale with clarity.',
    heroDesc: 'Early-stage startups move fast — but speed without structure quickly turns into chaos. Outsprinter helps small teams stay aligned, focused, and execution-driven from day one, without introducing heavy processes or enterprise complexity.',
    challengeTitle: 'The Challenge for Small Startups',
    challengeIntro: 'Startups typically struggle with:',
    ch1: 'Too many ideas, not enough focus',
    ch2: 'Goals that change weekly without tracking',
    ch3: 'KPIs living in spreadsheets or not tracked at all',
    ch4: 'Founders carrying everything in their heads',
    ch5: 'Teams working hard, but not always on the right things',
    challengeConclusion: 'Execution becomes reactive instead of intentional.',
    howTitle: 'How Outsprinter Helps Small Teams Win',
    howIntro: 'Outsprinter gives startups a lightweight execution system that scales with them.',
    howSubIntro: 'With Outsprinter, startups can:',
    how1: 'Define clear company goals and KPIs',
    how2: 'Translate strategy into weekly sprints',
    how3: 'Track progress without bureaucracy',
    how4: 'Keep everyone aligned as the team grows',
    how5: 'Build execution discipline early',
    howConclusion: 'No project management overload. No KPI chaos.',
    workflowTitle: 'Typical Startup Workflow in Outsprinter',
    wf1Title: 'Week 1 – Setup',
    wf1a: 'Founder sets company-level KPIs',
    wf1b: 'Create 2–3 teams (e.g. Product, Sales, Ops)',
    wf1c: 'Define ownership for each KPI',
    wf1d: 'Invite team members',
    wf2Title: 'Weekly Rhythm',
    wf2a: 'Plan a weekly sprint (top 3 priorities only)',
    wf2b: 'Update KPIs once per week',
    wf2c: 'Review sprint results in 10–15 minutes',
    wf2d: 'AI generates a summary and improvement suggestions',
    wf3Title: 'Leadership View',
    wf3a: 'Founder sees company health in one dashboard',
    wf3b: "Knows immediately what's working and what's not",
    featuresTitle: 'Key Features for Small Startups',
    feat1Title: 'KPI Manager',
    feat1a: 'Simple KPI setup',
    feat1b: 'Visual KPI Tree',
    feat1c: 'Automatic status indicators (green / yellow / red)',
    feat2Title: 'Team Sprint Manager',
    feat2a: 'Weekly sprint planning',
    feat2b: 'Clear ownership',
    feat2c: 'Focus on what truly matters',
    feat3Title: 'Smart AI Assistant',
    feat3a: 'KPI suggestions',
    feat3b: 'Weekly summaries',
    feat3c: 'Execution insights',
    feat4Title: 'Dashboards',
    feat4a: 'Company overview',
    feat4b: 'Team-level progress',
    feat4c: 'Weekly execution health',
    resultsTitle: 'Results Startups Achieve',
    resultsIntro: 'Startups using Outsprinter typically experience:',
    res1: 'Faster decision-making',
    res2: 'Clearer priorities',
    res3: 'Better founder visibility',
    res4: 'Reduced chaos as the team grows',
    res5: 'Strong execution habits from day one',
    resultsConclusion: 'Outsprinter helps founders stop guessing — and start leading with data.',
    whyTitle: 'Why Outsprinter Is a Perfect Fit for Early Teams',
    why1: 'Fast setup (under 30 minutes)',
    why2: 'Minimal overhead',
    why3: 'No complex configurations',
    why4: 'Grows with your company',
    why5: 'Designed for execution, not reporting',
    whyConclusion: "You don't need a complex system. You need clarity and consistency.",
    ctaTitle: 'Build execution discipline from the start.',
  },

  // Use Case - SMB
  ucSMB: {
    seoTitle: 'Small & Medium Companies (21-200 Employees)',
    seoDesc: 'Create alignment across teams. Execute at scale. Stay in control. Outsprinter gives growing companies a clear execution system.',
    badge: 'Small & Medium Companies (21–200 Employees)',
    heroTitle: 'Create alignment across teams. Execute at scale. Stay in control.',
    heroDesc: 'As companies grow beyond the startup phase, execution becomes harder — not easier. More teams, more managers, more priorities, and more data often lead to misalignment and slower decisions. Outsprinter gives growing companies a clear execution system that keeps leadership, teams, and KPIs aligned — without adding bureaucracy.',
    challengeTitle: 'The Challenge for Growing Companies',
    challengeIntro: 'SMBs typically struggle with:',
    ch1: 'Teams working in silos',
    ch2: 'Department goals not aligned with company strategy',
    ch3: 'KPIs tracked inconsistently or too late',
    ch4: 'Weekly meetings without real data',
    ch5: 'Leaders reacting instead of leading',
    challengeConclusion: 'Growth exposes execution gaps.',
    howTitle: 'How Outsprinter Brings Structure Without Slowing You Down',
    howIntro: "Outsprinter introduces structure where it's needed — and flexibility where it matters.",
    howSubIntro: 'With Outsprinter, SMBs can:',
    how1: 'Connect company strategy to department KPIs',
    how2: 'Run consistent weekly sprints across teams',
    how3: 'Track execution in real time',
    how4: 'Identify risks early',
    how5: 'Maintain speed while scaling',
    howConclusion: 'Execution becomes intentional, visible, and repeatable.',
    workflowTitle: 'Typical SMB Workflow in Outsprinter',
    wf1Title: 'Company Setup',
    wf1a: 'Leadership defines company-level KPIs',
    wf1b: 'Departments create aligned KPIs',
    wf1c: 'Owners assigned for every metric',
    wf2Title: 'Weekly Execution',
    wf2a: 'Teams plan weekly sprints',
    wf2b: 'KPIs updated once per week',
    wf2c: 'Managers review dashboards',
    wf2d: 'AI highlights risks and improvement areas',
    wf3Title: 'Leadership Review',
    wf3a: 'CEO reviews company dashboard',
    wf3b: 'Red KPIs trigger focused discussions',
    wf3c: 'Decisions are made based on facts',
    featuresTitle: 'Key Features for SMBs',
    feat1Title: 'KPI Tree',
    feat1a: 'Clear hierarchy from company → department → KPI',
    feat1b: 'Visual alignment across the organization',
    feat2Title: 'Weekly Sprint Management',
    feat2a: 'Structured sprint planning',
    feat2b: 'Focused execution',
    feat2c: 'Measurable outcomes',
    feat3Title: 'Dashboards',
    feat3a: 'Company-wide overview',
    feat3b: 'Department performance dashboards',
    feat3c: 'Trend analysis',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Weekly performance summaries',
    feat4b: 'Risk detection',
    feat4c: 'Execution insights',
    resultsTitle: 'Measurable Outcomes for SMBs',
    resultsIntro: 'Companies typically see:',
    res1: 'Stronger cross-team alignment',
    res2: 'Faster leadership decisions',
    res3: 'Fewer surprises in performance',
    res4: 'Clear ownership of results',
    res5: 'Higher execution consistency',
    resultsConclusion: 'Outsprinter turns growth into a competitive advantage — not a risk.',
    whyTitle: 'Why Outsprinter Fits Growing Organizations',
    why1: 'Designed for multi-team environments',
    why2: 'Scales without re-implementations',
    why3: 'Works for leadership and teams alike',
    why4: 'Enforces discipline without micromanagement',
    whyConclusion: "It's an execution system, not just another tool.",
    ctaTitle: 'Scale execution without losing clarity.',
  },

  // Use Case - Enterprise
  ucEnterprise: {
    seoTitle: 'Enterprise',
    seoDesc: 'Enterprise-grade execution, visibility, and accountability — at scale. Connect strategy, KPIs, teams, and leadership into one transparent platform.',
    badge: 'Enterprise',
    heroTitle: 'Enterprise-grade execution, visibility, and accountability — at scale.',
    heroDesc: 'Large organizations don\'t struggle with strategy — they struggle with execution at scale. Multiple business units, complex structures, and delayed visibility often lead to slow decisions and missed opportunities. Outsprinter gives enterprises a single execution operating system that connects strategy, KPIs, teams, and leadership into one transparent, real-time platform.',
    challengeTitle: 'The Enterprise Execution Challenge',
    challengeIntro: 'Enterprise organizations commonly face:',
    ch1: 'Disconnected business units and KPIs',
    ch2: 'Delayed or filtered performance reporting',
    ch3: 'Too many tools and fragmented data',
    ch4: 'Slow reaction to underperformance',
    ch5: 'Lack of real-time visibility for executives',
    challengeConclusion: 'Execution becomes complex, opaque, and reactive.',
    howTitle: 'How Outsprinter Supports Enterprise Execution',
    howIntro: 'Outsprinter is designed to bring clarity and structure without forcing a one-size-fits-all process.',
    howSubIntro: 'With Outsprinter, enterprises can:',
    how1: 'Align corporate strategy with business unit KPIs',
    how2: 'Maintain consistent execution cadence across departments',
    how3: 'Provide executives with real-time visibility',
    how4: 'Detect risks earlier through AI analysis',
    how5: 'Standardize execution while allowing local autonomy',
    workflowTitle: 'Typical Enterprise Workflow in Outsprinter',
    wf1Title: 'Strategic Alignment',
    wf1a: 'Board and executive team define top-level objectives',
    wf1b: 'Business units create aligned KPIs',
    wf1c: 'Ownership assigned across all levels',
    wf2Title: 'Operational Execution',
    wf2a: 'Teams run weekly sprints within defined frameworks',
    wf2b: 'KPIs updated consistently',
    wf2c: 'AI monitors trends and anomalies',
    wf3Title: 'Executive Oversight',
    wf3a: 'Executives view company-wide dashboards',
    wf3b: 'Red KPIs trigger immediate focus',
    wf3c: 'Forecast dashboards support strategic decisions',
    featuresTitle: 'Enterprise-Grade Features',
    feat1Title: 'Advanced KPI Frameworks',
    feat1a: 'Multi-level KPI trees',
    feat1b: 'Custom KPI frameworks by business unit',
    feat1c: 'Aggregated and drill-down views',
    feat2Title: 'Leadership Dashboards',
    feat2a: 'Board-level dashboards',
    feat2b: 'Executive summaries',
    feat2c: 'Trend and forecast analysis',
    feat3Title: 'Security & Compliance',
    feat3a: 'Role-based access control',
    feat3b: 'Audit logs',
    feat3c: 'Single Sign-On (SSO)',
    feat3d: 'Data residency options',
    feat4Title: 'AI Insights',
    feat4a: 'Predictive risk analysis',
    feat4b: 'Cross-unit performance comparisons',
    feat4c: 'Execution bottleneck detection',
    resultsTitle: 'Enterprise Outcomes',
    resultsIntro: 'Organizations typically achieve:',
    res1: 'Faster executive decision-making',
    res2: 'Clear accountability across units',
    res3: 'Reduced reporting overhead',
    res4: 'Better execution consistency',
    res5: 'Increased organizational trust',
    resultsConclusion: 'Outsprinter turns complexity into clarity.',
    whyTitle: 'Designed for Enterprise Reality',
    why1: 'Supports complex org structures',
    why2: 'Flexible governance models',
    why3: 'Enterprise-level security',
    why4: 'Dedicated onboarding and support',
    whyConclusion: 'Outsprinter adapts to how enterprises operate — not the other way around.',
    ctaTitle: 'Bring clarity to enterprise execution.',
    ctaBtn1: 'Request Enterprise Demo',
    ctaBtn2: 'Talk to Sales',
  },

  // Use Case - IT Teams
  ucIT: {
    seoTitle: 'IT Teams',
    seoDesc: 'Turn technical effort into measurable business impact. Align technical execution with business goals and track meaningful KPIs.',
    badge: 'IT Teams',
    heroTitle: 'Turn technical effort into measurable business impact.',
    heroDesc: 'IT teams are under constant pressure to deliver fast, maintain stability, and support the business — often with changing priorities and limited visibility into what truly matters. Outsprinter helps IT teams align technical execution with business goals, track meaningful KPIs, and run focused weekly sprints without unnecessary overhead.',
    challengeTitle: 'The Challenge for IT Teams',
    challengeIntro: 'IT and engineering teams often face:',
    ch1: 'Unclear or shifting priorities',
    ch2: 'Too many tasks, not enough focus',
    ch3: "KPIs that don't reflect real impact",
    ch4: 'Sprint work disconnected from business outcomes',
    ch5: 'Leadership not fully understanding delivery constraints',
    challengeConclusion: 'This leads to frustration, overload, and misalignment.',
    howTitle: 'How Outsprinter Empowers IT Teams',
    howIntro: 'Outsprinter connects technical execution directly to company objectives.',
    howSubIntro: 'With Outsprinter, IT teams can:',
    how1: 'Define clear technical KPIs tied to business goals',
    how2: 'Run structured weekly sprints with visible outcomes',
    how3: 'Track delivery performance and reliability metrics',
    how4: 'Communicate progress clearly to leadership',
    how5: 'Reduce context switching and firefighting',
    howConclusion: 'Execution becomes predictable and transparent.',
    workflowTitle: 'Typical IT Team Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define technical KPIs (uptime, velocity, incident response, delivery lead time)',
    wf1b: 'Align KPIs with company objectives',
    wf1c: 'Assign ownership',
    wf2Title: 'Weekly Execution',
    wf2a: 'Plan weekly sprint priorities',
    wf2b: 'Track sprint progress',
    wf2c: 'Update KPIs once per week',
    wf2d: 'AI highlights risks and bottlenecks',
    wf3Title: 'Leadership Reporting',
    wf3a: 'IT dashboards show delivery health',
    wf3b: 'Executives see impact without micromanagement',
    featuresTitle: 'Key Features for IT Teams',
    feat1Title: 'KPI Manager',
    feat1a: 'Technical KPI templates',
    feat1b: 'Trend tracking',
    feat1c: 'SLA & reliability metrics',
    feat2Title: 'Sprint Manager',
    feat2a: 'Weekly sprint planning',
    feat2b: 'Delivery tracking',
    feat2c: 'Capacity awareness',
    feat3Title: 'Dashboards',
    feat3a: 'Sprint health dashboard',
    feat3b: 'Technical performance trends',
    feat3c: 'Delivery velocity metrics',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Sprint risk detection',
    feat4b: 'Overcommitment alerts',
    feat4c: 'Delivery pattern analysis',
    resultsTitle: 'Results IT Teams Achieve',
    resultsIntro: 'Teams typically see:',
    res1: 'Clearer priorities',
    res2: 'Better sprint predictability',
    res3: 'Reduced firefighting',
    res4: 'Improved communication with leadership',
    res5: 'KPIs that reflect real value',
    resultsConclusion: 'Outsprinter helps IT teams prove impact — not just activity.',
    whyTitle: 'Why Outsprinter Works for IT',
    why1: 'Lightweight compared to heavy project tools',
    why2: 'KPI-driven, not task-driven',
    why3: 'Complements tools like Jira, not replaces them',
    why4: 'Built for clarity and execution',
    ctaTitle: 'Align technology with outcomes.',
  },

  // Use Case - Operations
  ucOps: {
    seoTitle: 'Operations Teams',
    seoDesc: 'Make operations measurable, predictable, and continuously improving. Track performance weekly and improve execution with clarity.',
    badge: 'Operations Teams',
    heroTitle: 'Make operations measurable, predictable, and continuously improving.',
    heroDesc: 'Operations teams keep the business running — but their impact is often hidden behind complexity, manual reporting, and reactive problem-solving. Outsprinter helps operations teams define the right KPIs, track performance weekly, and improve execution with clarity and discipline.',
    challengeTitle: 'The Challenge for Operations Teams',
    challengeIntro: 'Operations teams often struggle with:',
    ch1: 'Too many metrics without clear priorities',
    ch2: 'Performance issues discovered too late',
    ch3: 'Manual reporting and spreadsheets',
    ch4: 'Lack of visibility across teams and processes',
    ch5: 'Constant firefighting instead of improvement',
    challengeConclusion: 'This leads to inefficiency and unnecessary risk.',
    howTitle: 'How Outsprinter Strengthens Operations Execution',
    howIntro: 'Outsprinter gives operations teams a system to:',
    how1: 'Define operational KPIs that truly matter',
    how2: 'Track performance weekly instead of monthly',
    how3: 'Identify bottlenecks early',
    how4: 'Align teams around improvement priorities',
    how5: 'Communicate performance clearly to leadership',
    howConclusion: 'Operations become proactive — not reactive.',
    workflowTitle: 'Typical Operations Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define operational KPIs (cycle time, cost efficiency, quality, throughput)',
    wf1b: 'Assign KPI ownership',
    wf1c: 'Align KPIs with company goals',
    wf2Title: 'Weekly Cadence',
    wf2a: 'Update KPIs weekly',
    wf2b: 'Review operational dashboards',
    wf2c: 'Plan improvement sprints',
    wf2d: 'AI highlights trends and risks',
    wf3Title: 'Leadership Alignment',
    wf3a: 'Share clear operational dashboards',
    wf3b: 'Focus discussions on red and yellow KPIs',
    featuresTitle: 'Key Features for Operations Teams',
    feat1Title: 'KPI Manager',
    feat1a: 'Operational KPI templates',
    feat1b: 'Trend analysis',
    feat1c: 'Threshold-based alerts',
    feat2Title: 'Sprint Manager',
    feat2a: 'Improvement sprints',
    feat2b: 'Process optimization tracking',
    feat2c: 'Clear accountability',
    feat3Title: 'Dashboards',
    feat3a: 'Operational performance overview',
    feat3b: 'Trend dashboards',
    feat3c: 'Efficiency heatmaps',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Bottleneck detection',
    feat4b: 'Performance anomaly alerts',
    feat4c: 'Improvement recommendations',
    resultsTitle: 'Results Operations Teams Achieve',
    resultsIntro: 'Teams typically achieve:',
    res1: 'Reduced operational risk',
    res2: 'Faster issue detection',
    res3: 'Improved efficiency',
    res4: 'Clear accountability',
    res5: 'Continuous improvement culture',
    resultsConclusion: 'Outsprinter turns operations into a competitive advantage.',
    whyTitle: 'Why Outsprinter Fits Operations Teams',
    why1: 'KPI-first approach',
    why2: 'Weekly execution rhythm',
    why3: 'Clear visibility without micromanagement',
    why4: 'Designed for continuous improvement',
    ctaTitle: 'Run operations with confidence.',
  },

  // Use Case - Sales
  ucSales: {
    seoTitle: 'Sales Teams',
    seoDesc: 'Turn sales activity into predictable revenue and clear accountability. Track pipeline health weekly and give leadership real-time visibility.',
    badge: 'Sales Teams',
    heroTitle: 'Turn sales activity into predictable revenue and clear accountability.',
    heroDesc: 'Sales teams are measured by results — but too often visibility comes too late. Pipelines look healthy until they don\'t, forecasts are based on assumptions, and execution gaps surface only at month-end. Outsprinter helps sales teams define the right KPIs, run focused weekly sprints, and give leadership real-time visibility into revenue performance.',
    challengeTitle: 'The Challenge for Sales Teams',
    challengeIntro: 'Sales organizations commonly face:',
    ch1: 'Activity-heavy, outcome-light reporting',
    ch2: 'KPIs reviewed monthly instead of weekly',
    ch3: 'Forecasts based on gut feeling',
    ch4: 'Poor alignment between sales strategy and daily execution',
    ch5: 'Limited visibility for leadership',
    challengeConclusion: 'This creates volatility and missed targets.',
    howTitle: 'How Outsprinter Improves Sales Execution',
    howIntro: 'Outsprinter connects sales strategy to weekly action.',
    howSubIntro: 'With Outsprinter, sales teams can:',
    how1: 'Define outcome-focused sales KPIs',
    how2: 'Track pipeline health weekly',
    how3: 'Run focused sales sprints',
    how4: 'Identify risks early',
    how5: 'Align sales activity with revenue goals',
    howConclusion: 'Execution becomes measurable, transparent, and consistent.',
    workflowTitle: 'Typical Sales Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define sales KPIs (pipeline value, conversion rate, win rate, deal velocity, ARR/MRR)',
    wf1b: 'Align KPIs with company revenue goals',
    wf1c: 'Assign clear ownership',
    wf2Title: 'Weekly Sales Cadence',
    wf2a: 'Plan weekly sales sprints (top deals, key actions)',
    wf2b: 'Update KPIs weekly',
    wf2c: 'Review dashboard performance',
    wf2d: 'AI highlights risks and opportunities',
    wf3Title: 'Leadership Review',
    wf3a: 'Sales leaders and CEOs see real-time dashboards',
    wf3b: 'Underperforming KPIs trigger focused action',
    featuresTitle: 'Key Features for Sales Teams',
    feat1Title: 'KPI Manager',
    feat1a: 'Revenue-focused KPI templates',
    feat1b: 'Pipeline and conversion tracking',
    feat1c: 'Trend analysis',
    feat2Title: 'Sprint Manager',
    feat2a: 'Weekly deal-focused sprints',
    feat2b: 'Clear priorities',
    feat2c: 'Execution tracking',
    feat3Title: 'Dashboards',
    feat3a: 'Sales performance dashboard',
    feat3b: 'Pipeline health overview',
    feat3c: 'Forecast trends',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Deal risk detection',
    feat4b: 'Pipeline quality analysis',
    feat4c: 'Forecast accuracy insights',
    resultsTitle: 'Results Sales Teams Achieve',
    resultsIntro: 'Sales teams typically see:',
    res1: 'More predictable revenue',
    res2: 'Higher forecast accuracy',
    res3: 'Clear sales priorities',
    res4: 'Faster reaction to pipeline risks',
    res5: 'Better alignment with leadership',
    resultsConclusion: 'Outsprinter helps sales teams focus on closing — not reporting.',
    whyTitle: 'Why Outsprinter Works for Sales',
    why1: 'KPI-driven, not activity-driven',
    why2: 'Weekly cadence instead of monthly surprises',
    why3: 'Clear leadership visibility',
    why4: 'Complements CRMs like HubSpot or Pipedrive',
    ctaTitle: 'Make revenue execution predictable.',
  },

  // Use Case - Management Board
  ucBoard: {
    seoTitle: 'Management Board',
    seoDesc: 'Board-level clarity. Real-time insight. Confident decisions. Get a clear, real-time view of company performance and strategic progress.',
    badge: 'Management Board',
    heroTitle: 'Board-level clarity. Real-time insight. Confident decisions.',
    heroDesc: 'Boards and executive teams are responsible for direction, oversight, and results — yet too often they receive delayed, filtered, or overly complex reports that make real decision-making difficult. Outsprinter gives management boards a clear, real-time view of company performance, execution risks, and strategic progress — without drowning in operational detail.',
    challengeTitle: 'The Challenge for Management Boards',
    challengeIntro: 'Boards and executive teams commonly face:',
    ch1: 'Performance data arriving too late',
    ch2: 'Reports filtered through multiple layers',
    ch3: 'Inconsistent KPI definitions across departments',
    ch4: 'Difficulty identifying real risks early',
    ch5: 'Meetings focused on explanations instead of decisions',
    challengeConclusion: 'This weakens governance and slows strategic action.',
    howTitle: 'How Outsprinter Supports Boards & Executives',
    howIntro: 'Outsprinter is designed to surface truth, not noise.',
    howSubIntro: 'With Outsprinter, boards can:',
    how1: 'See company-level KPIs in real time',
    how2: 'Track execution against strategic objectives',
    how3: 'Identify risks before they escalate',
    how4: 'Compare trends across departments',
    how5: 'Focus discussions on outcomes, not opinions',
    howConclusion: 'It enables boards to govern proactively — not reactively.',
    workflowTitle: 'Typical Board Workflow in Outsprinter',
    wf1Title: 'Strategic Setup',
    wf1a: 'Company strategy translated into top-level KPIs',
    wf1b: 'Clear ownership assigned to leadership roles',
    wf1c: 'KPI targets approved at board level',
    wf2Title: 'Ongoing Oversight',
    wf2a: 'KPIs updated weekly by management',
    wf2b: 'Board dashboards auto-update in real time',
    wf2c: 'AI flags risks, deviations, and anomalies',
    wf3Title: 'Board Meetings',
    wf3a: 'Meetings start with a live dashboard',
    wf3b: 'Red and yellow KPIs drive discussion',
    wf3c: 'Decisions are data-driven and focused',
    featuresTitle: 'Key Features for Management Boards',
    feat1Title: 'Board-Level Dashboards',
    feat1a: 'High-level KPI overview',
    feat1b: 'Trend and forecast views',
    feat1c: 'Risk indicators',
    feat2Title: 'Drill-Down Capability',
    feat2a: 'From company KPIs to department performance',
    feat2b: 'Without exposing unnecessary operational noise',
    feat3Title: 'AI Insights',
    feat3a: 'Early warning signals',
    feat3b: 'Performance pattern analysis',
    feat3c: 'Strategic execution risks',
    feat4Title: 'Governance & Transparency',
    feat4a: 'Consistent metrics across the organization',
    feat4b: 'Clear accountability',
    feat4c: 'Audit-ready reporting',
    resultsTitle: 'Outcomes for Boards & Executives',
    resultsIntro: 'Boards using Outsprinter typically experience:',
    res1: 'Better strategic oversight',
    res2: 'Faster, higher-quality decisions',
    res3: 'Fewer surprises',
    res4: 'More effective board meetings',
    res5: 'Increased trust between board and management',
    resultsConclusion: 'Outsprinter strengthens governance by making performance visible and objective.',
    whyTitle: 'Why Outsprinter Fits Board-Level Use',
    why1: 'Designed for executive attention spans',
    why2: 'Focuses on outcomes, not activity',
    why3: 'Provides clarity without micromanagement',
    why4: 'Supports long-term strategic thinking',
    ctaTitle: 'Lead with clarity. Govern with confidence.',
    ctaBtn1: 'Request a Board Demo',
    ctaBtn2: 'Talk to Sales',
  },

  // Use Case - Project Management
  ucProject: {
    seoTitle: 'Project Management',
    seoDesc: 'Deliver projects on time, on scope, and with measurable impact. Align project execution with company goals and track delivery through meaningful KPIs.',
    badge: 'Project Management',
    heroTitle: 'Deliver projects on time, on scope, and with measurable impact.',
    heroDesc: 'Projects fail not because teams don\'t work hard — but because priorities shift, progress isn\'t tracked clearly, and delivery loses connection to business outcomes. Outsprinter helps project teams align project execution with company goals, run focused weekly sprints, and track delivery through meaningful KPIs.',
    challengeTitle: 'The Challenge in Project Management',
    challengeIntro: 'Project teams often struggle with:',
    ch1: 'Scope creep',
    ch2: 'Unclear priorities',
    ch3: 'Progress reported too late',
    ch4: 'Projects disconnected from strategic goals',
    ch5: 'Teams working hard without knowing if they\'re winning',
    challengeConclusion: 'This leads to delays, cost overruns, and stakeholder frustration.',
    howTitle: 'How Outsprinter Improves Project Execution',
    howIntro: 'Outsprinter connects projects to outcomes.',
    howSubIntro: 'With Outsprinter, project teams can:',
    how1: 'Define project KPIs tied to business value',
    how2: 'Run structured weekly sprints',
    how3: 'Track progress in real time',
    how4: 'Identify risks early',
    how5: 'Keep stakeholders aligned with live dashboards',
    howConclusion: 'Execution becomes predictable and transparent.',
    workflowTitle: 'Typical Project Workflow in Outsprinter',
    wf1Title: 'Project Setup',
    wf1a: 'Define project objectives and KPIs',
    wf1b: 'Align project KPIs with company goals',
    wf1c: 'Assign owners and timelines',
    wf2Title: 'Weekly Execution',
    wf2a: 'Plan weekly sprints',
    wf2b: 'Track task completion',
    wf2c: 'Update KPIs',
    wf2d: 'AI highlights delivery risks',
    wf3Title: 'Stakeholder Reporting',
    wf3a: 'Share live dashboards',
    wf3b: 'Eliminate manual status reports',
    featuresTitle: 'Key Features for Project Management',
    feat1Title: 'KPI Manager',
    feat1a: 'Project KPI templates',
    feat1b: 'Progress and quality tracking',
    feat2Title: 'Sprint Manager',
    feat2a: 'Weekly sprint planning',
    feat2b: 'Scope control',
    feat2c: 'Delivery tracking',
    feat3Title: 'Dashboards',
    feat3a: 'Project health dashboard',
    feat3b: 'Timeline and progress visualization',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Scope creep detection',
    feat4b: 'Delivery risk analysis',
    feat4c: 'Weekly status summaries',
    resultsTitle: 'Results Project Teams Achieve',
    resultsIntro: 'Teams typically see:',
    res1: 'Higher on-time delivery rates',
    res2: 'Fewer last-minute surprises',
    res3: 'Clear accountability',
    res4: 'Better stakeholder communication',
    res5: 'Stronger link between projects and strategy',
    resultsConclusion: 'Outsprinter helps projects deliver real business value.',
    whyTitle: 'Why Outsprinter Works for Project Teams',
    why1: 'Outcome-driven, not task-heavy',
    why2: 'Lightweight and fast to adopt',
    why3: 'Works across multiple projects',
    why4: 'Clear executive visibility',
    ctaTitle: 'Deliver projects with confidence.',
  },

  // Use Case - Public Institutions
  ucPublic: {
    seoTitle: 'Public Institutions',
    seoDesc: 'Transparency, accountability, and measurable impact for the public sector. Track performance consistently and demonstrate impact with data-driven clarity.',
    badge: 'Public Institutions',
    heroTitle: 'Transparency, accountability, and measurable impact for the public sector.',
    heroDesc: 'Public institutions operate under high expectations for accountability, transparency, and results — often with limited resources and complex governance structures. Outsprinter helps public-sector organizations define clear objectives, track performance consistently, and demonstrate impact with data-driven clarity.',
    challengeTitle: 'The Challenge for Public Institutions',
    challengeIntro: 'Public organizations often face:',
    ch1: 'Ambiguous or hard-to-measure objectives',
    ch2: 'Fragmented reporting across departments',
    ch3: 'Limited real-time visibility into performance',
    ch4: 'Manual, time-consuming reporting processes',
    ch5: 'High accountability demands with low tolerance for error',
    challengeConclusion: 'This makes effective management difficult.',
    howTitle: 'How Outsprinter Supports Public-Sector Execution',
    howIntro: 'Outsprinter brings structure, clarity, and transparency to public institutions.',
    howSubIntro: 'With Outsprinter, public organizations can:',
    how1: 'Define measurable KPIs aligned with policy goals',
    how2: 'Track progress weekly instead of quarterly',
    how3: 'Improve cross-department coordination',
    how4: 'Provide leadership and stakeholders with clear dashboards',
    how5: 'Increase trust through transparent reporting',
    howConclusion: 'Execution becomes measurable and defensible.',
    workflowTitle: 'Typical Public-Sector Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define strategic objectives and KPIs',
    wf1b: 'Align departmental KPIs',
    wf1c: 'Assign clear ownership',
    wf2Title: 'Ongoing Management',
    wf2a: 'Departments update KPIs weekly',
    wf2b: 'Dashboards reflect real-time progress',
    wf2c: 'AI flags delays and risks',
    wf3Title: 'Reporting',
    wf3a: 'Share dashboards with leadership and oversight bodies',
    wf3b: 'Export reports for audits and reviews',
    featuresTitle: 'Key Features for Public Institutions',
    feat1Title: 'KPI Manager',
    feat1a: 'Policy and service KPIs',
    feat1b: 'Outcome-based tracking',
    feat2Title: 'Dashboards',
    feat2a: 'Department-level transparency',
    feat2b: 'Program performance overviews',
    feat3Title: 'Smart AI Assistant',
    feat3a: 'Risk and delay detection',
    feat3b: 'Performance summaries',
    feat4Title: 'Compliance & Governance',
    feat4a: 'Audit logs',
    feat4b: 'Role-based access',
    feat4c: 'Data export',
    resultsTitle: 'Results Public Institutions Achieve',
    resultsIntro: 'Institutions typically experience:',
    res1: 'Greater transparency',
    res2: 'Better accountability',
    res3: 'Faster response to issues',
    res4: 'Improved trust with stakeholders',
    res5: 'Clear demonstration of impact',
    resultsConclusion: 'Outsprinter helps public organizations prove value — clearly and credibly.',
    whyTitle: 'Why Outsprinter Fits the Public Sector',
    why1: 'Designed for transparency',
    why2: 'Simple and structured',
    why3: 'Secure and compliant',
    why4: 'Scales across departments',
    ctaTitle: 'Bring clarity and accountability to public service.',
    ctaBtn1: 'Request a Demo',
    ctaBtn2: 'Contact Sales',
  },

  // Use Case - Nonprofits
  ucNonprofit: {
    seoTitle: 'Nonprofits',
    seoDesc: 'Maximize impact. Strengthen accountability. Prove outcomes. Define clear objectives, track meaningful KPIs, and align teams around outcomes that truly matter.',
    badge: 'Nonprofits',
    heroTitle: 'Maximize impact. Strengthen accountability. Prove outcomes.',
    heroDesc: 'Nonprofits operate with strong missions, limited resources, and high expectations for transparency. Yet impact is often difficult to measure, communicate, and improve consistently. Outsprinter helps nonprofits define clear objectives, track meaningful KPIs, and align teams around outcomes that truly matter — without adding administrative burden.',
    challengeTitle: 'The Challenge for Nonprofits',
    challengeIntro: 'Nonprofit organizations commonly face:',
    ch1: 'Impact that is hard to measure objectively',
    ch2: 'Donors and stakeholders asking for clear results',
    ch3: 'Teams spread across programs and initiatives',
    ch4: 'Manual reporting consuming valuable time',
    ch5: 'Limited visibility into what is actually working',
    challengeConclusion: 'This makes scaling impact difficult.',
    howTitle: 'How Outsprinter Helps Nonprofits Deliver More Impact',
    howIntro: 'Outsprinter provides a simple execution and measurement system for mission-driven work.',
    howSubIntro: 'With Outsprinter, nonprofits can:',
    how1: 'Define outcome-based KPIs aligned with their mission',
    how2: 'Track program performance weekly',
    how3: 'Align teams and volunteers around priorities',
    how4: 'Provide transparent reporting to donors and boards',
    how5: 'Focus more time on impact, less on administration',
    howConclusion: 'Mission execution becomes visible and measurable.',
    workflowTitle: 'Typical Nonprofit Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define strategic goals (mission outcomes, reach, effectiveness)',
    wf1b: 'Create program-level KPIs',
    wf1c: 'Assign ownership to teams or program leads',
    wf2Title: 'Weekly Rhythm',
    wf2a: 'Update KPIs weekly',
    wf2b: 'Review dashboards',
    wf2c: 'Plan focused improvement sprints',
    wf2d: 'AI highlights risks or declining impact',
    wf3Title: 'Reporting',
    wf3a: 'Share dashboards with boards and donors',
    wf3b: 'Export reports for funding reviews',
    featuresTitle: 'Key Features for Nonprofits',
    feat1Title: 'KPI Manager',
    feat1a: 'Mission and impact KPIs',
    feat1b: 'Program-level tracking',
    feat2Title: 'Dashboards',
    feat2a: 'Organization-wide impact overview',
    feat2b: 'Program performance dashboards',
    feat3Title: 'Smart AI Assistant',
    feat3a: 'Impact trend analysis',
    feat3b: 'Program effectiveness insights',
    feat4Title: 'Transparency & Governance',
    feat4a: 'Clear ownership',
    feat4b: 'Audit-ready reporting',
    feat4c: 'Role-based access',
    resultsTitle: 'Results Nonprofits Achieve',
    resultsIntro: 'Nonprofits using Outsprinter typically see:',
    res1: 'Clearer demonstration of impact',
    res2: 'Stronger accountability across programs',
    res3: 'Better board and donor confidence',
    res4: 'Improved focus on high-impact initiatives',
    res5: 'Reduced reporting overhead',
    resultsConclusion: 'Outsprinter helps nonprofits turn intention into measurable impact.',
    whyTitle: 'Why Outsprinter Fits Nonprofits',
    why1: 'Outcome-focused, not profit-driven',
    why2: 'Simple and easy to adopt',
    why3: 'Supports transparency and trust',
    why4: 'Affordable and scalable',
    ctaTitle: 'Deliver more impact with clarity.',
    ctaBtn2: 'Request a Demo',
  },

  // Use Case - HR Teams
  ucHR: {
    seoTitle: 'HR Teams',
    seoDesc: 'Align people, performance, and strategy — with clarity and data. Turn people strategy into measurable execution aligned with company goals.',
    badge: 'HR Teams',
    heroTitle: 'Align people, performance, and strategy — with clarity and data.',
    heroDesc: 'HR teams play a critical role in company success, yet their impact is often hard to measure. Goals like engagement, performance, and development are discussed — but not always tracked in a structured, outcome-driven way. Outsprinter helps HR teams turn people strategy into measurable execution, aligned with company goals and leadership expectations.',
    challengeTitle: 'The Challenge for HR Teams',
    challengeIntro: 'HR and People Ops teams often face:',
    ch1: 'Soft goals that are hard to quantify',
    ch2: 'Disconnected people initiatives',
    ch3: 'Limited visibility into team performance health',
    ch4: 'Manual reporting for leadership and boards',
    ch5: 'Difficulty linking HR efforts to business outcomes',
    challengeConclusion: "This reduces HR's strategic influence.",
    howTitle: 'How Outsprinter Elevates HR Execution',
    howIntro: 'Outsprinter gives HR teams a structured execution and measurement system.',
    howSubIntro: 'With Outsprinter, HR teams can:',
    how1: 'Define clear people-related KPIs',
    how2: 'Align HR initiatives with company strategy',
    how3: 'Track progress weekly instead of quarterly',
    how4: 'Provide leadership with transparent dashboards',
    how5: 'Identify risks early (engagement, turnover, capacity)',
    howConclusion: 'HR becomes proactive, data-driven, and strategic.',
    workflowTitle: 'Typical HR Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define HR KPIs (engagement, retention, hiring velocity, onboarding success, performance reviews)',
    wf1b: 'Align KPIs with company objectives',
    wf1c: 'Assign ownership',
    wf2Title: 'Weekly & Monthly Cadence',
    wf2a: 'Update KPIs regularly',
    wf2b: 'Track initiatives through sprints',
    wf2c: 'AI highlights trends and risks',
    wf3Title: 'Leadership Reporting',
    wf3a: 'Share HR dashboards with executives',
    wf3b: 'Support decisions with real-time data',
    featuresTitle: 'Key Features for HR Teams',
    feat1Title: 'KPI Manager',
    feat1a: 'People and culture KPI templates',
    feat1b: 'Trend and threshold tracking',
    feat2Title: 'Sprint Manager',
    feat2a: 'HR initiative planning',
    feat2b: 'Hiring and onboarding sprints',
    feat2c: 'Performance review cycles',
    feat3Title: 'Dashboards',
    feat3a: 'Engagement and retention dashboards',
    feat3b: 'Hiring pipeline overview',
    feat3c: 'Team health indicators',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Attrition risk signals',
    feat4b: 'Engagement trend analysis',
    feat4c: 'Recommendations for people initiatives',
    resultsTitle: 'Results HR Teams Achieve',
    resultsIntro: 'HR teams using Outsprinter typically achieve:',
    res1: 'Stronger alignment with leadership',
    res2: 'Clear visibility into people performance',
    res3: 'Earlier detection of retention risks',
    res4: 'More effective HR initiatives',
    res5: 'Increased strategic credibility',
    resultsConclusion: 'Outsprinter helps HR move from support function to strategic partner.',
    whyTitle: 'Why Outsprinter Works for HR',
    why1: 'Outcome-focused, not just activity-based',
    why2: 'Transparent and leadership-friendly',
    why3: 'Easy to adopt across the organization',
    why4: 'Connects people metrics to business goals',
    ctaTitle: 'Turn people strategy into measurable impact.',
  },

  // Use Case - Manufacturing
  ucManuf: {
    seoTitle: 'Production & Manufacturing',
    seoDesc: 'Turn production data into predictable performance and continuous improvement. Track OEE, throughput, quality, and delivery with real-time visibility.',
    badge: 'Production & Manufacturing',
    heroTitle: 'Turn production data into predictable performance and continuous improvement.',
    heroDesc: 'Production and manufacturing companies operate in complex environments where efficiency, quality, cost control, and delivery reliability must work together every day. When KPIs are reviewed too late or execution is not aligned, small issues quickly turn into costly problems. Outsprinter helps manufacturing organizations manage production performance with clarity, discipline, and real-time visibility.',
    challengeTitle: 'The Challenge for Production Companies',
    challengeIntro: 'Manufacturing and production teams often struggle with:',
    ch1: 'KPIs tracked in separate systems or spreadsheets',
    ch2: 'Performance issues discovered too late',
    ch3: 'Limited visibility across shifts, lines, or plants',
    ch4: 'Too many metrics without clear priorities',
    ch5: 'Reactive firefighting instead of continuous improvement',
    challengeConclusion: 'This leads to downtime, quality issues, missed delivery targets, and rising costs.',
    howTitle: 'How Outsprinter Improves Production Execution',
    howIntro: 'Outsprinter brings structure and transparency to production performance. With Outsprinter, production companies can:',
    how1: 'Define clear production KPIs aligned with business goals',
    how2: 'Track performance weekly with trend visibility',
    how3: 'Align production, maintenance, quality, and operations teams',
    how4: 'Identify bottlenecks and risks early',
    how5: 'Support continuous improvement with data',
    howConclusion: 'Execution becomes proactive, measurable, and consistent.',
    workflowTitle: 'Typical Production Workflow in Outsprinter',
    wf1Title: 'Setup',
    wf1a: 'Define production KPIs (OEE, throughput, scrap rate, downtime, lead time, on-time delivery)',
    wf1b: 'Align KPIs with company-level objectives',
    wf1c: 'Assign clear ownership per KPI',
    wf2Title: 'Weekly Cadence',
    wf2a: 'Update KPIs weekly',
    wf2b: 'Review production dashboards',
    wf2c: 'Plan improvement sprints (process, quality, maintenance)',
    wf2d: 'AI highlights negative trends and anomalies',
    wf3Title: 'Leadership Review',
    wf3a: 'Management reviews plant and line performance',
    wf3b: 'Focus on red and yellow KPIs',
    wf3c: 'Decisions are made based on facts, not assumptions',
    featuresTitle: 'Key Features for Production & Manufacturing',
    feat1Title: 'KPI Manager',
    feat1a: 'Production KPI templates',
    feat1b: 'Trend analysis across weeks and months',
    feat1c: 'Threshold-based alerts',
    feat2Title: 'Sprint Manager',
    feat2a: 'Continuous improvement sprints',
    feat2b: 'Maintenance and process optimization tracking',
    feat2c: 'Clear ownership and accountability',
    feat3Title: 'Dashboards',
    feat3a: 'Plant-level performance dashboards',
    feat3b: 'Line and process overview',
    feat3c: 'Efficiency and quality heatmaps',
    feat4Title: 'Smart AI Assistant',
    feat4a: 'Bottleneck and downtime pattern detection',
    feat4b: 'Quality deviation alerts',
    feat4c: 'Predictive risk insights',
    resultsTitle: 'Results Production Companies Achieve',
    resultsIntro: 'Manufacturing organizations using Outsprinter typically see:',
    res1: 'Improved production efficiency',
    res2: 'Reduced downtime and waste',
    res3: 'Better on-time delivery',
    res4: 'Faster issue detection',
    res5: 'Stronger cross-team alignment',
    resultsConclusion: 'Outsprinter helps production teams shift from reactive problem-solving to structured improvement.',
    whyTitle: 'Why Outsprinter Fits Manufacturing Environments',
    why1: 'KPI-first, execution-driven approach',
    why2: 'Simple weekly cadence that fits production reality',
    why3: 'Clear visibility across teams and shifts',
    why4: 'Scales from single plant to multi-site operations',
    ctaTitle: 'Run production with clarity and control.',
  },

  // Blog Post UI
  blogPost: {
    home: 'Home',
    backToBlog: 'Back to Blog',
    linkCopied: 'Link copied!',
    linkCopiedDesc: 'The article link has been copied to your clipboard.',
    topicsCovered: 'Topics covered:',
    shareArticle: 'Share this article:',
    aboutAuthor: 'About {{name}}',
    authorBio: 'Entrepreneur, angel investor, and founder of Outsprinter. Passionate about building scalable businesses with clear execution and measurable results.',
    connectLinkedIn: 'Connect on LinkedIn',
    relatedArticles: 'Related Articles',
    ctaTitle: 'Start Tracking KPIs the Right Way',
    ctaDesc: 'Put these insights into practice with Outsprinter. Weekly tracking, visual dashboards, and clear accountability.',
    ctaBtn: 'Start Your Free Trial',
    allRights: 'All rights reserved.',
  },

  cookies: {
    message: 'We use cookies and analytics to improve your experience. By accepting, you allow us to collect anonymous usage data via Google Analytics.',
    learnMore: 'Learn more',
    decline: 'Decline',
    accept: 'Accept',
  },
};

export default en;
